import React, { useEffect, useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../auth/msalConfig";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { api } from "../api";

export default function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const membershipType = location.state?.membershipType || "";

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    password: "",
    confirm_password: "",
  });

  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const finishMicrosoftSignup = async () => {
      try {
        const existingMember = localStorage.getItem("member");
        if (existingMember) return;

        const redirectResult = await instance.handleRedirectPromise();

        let account = redirectResult?.account;

        if (!account) {
          const accounts = instance.getAllAccounts();
          if (accounts.length > 0) {
            account = accounts[0];
          }
        }

        if (!account) return;

        setLoading(true);
        setMessage("");

        const tokenResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          account,
        });

        const accessToken = tokenResponse?.accessToken;

        if (!accessToken) {
          setMessage("Microsoft signup failed. No access token received.");
          return;
        }

        const result = await api.post("microsoftLogin.php", {
          access_token: accessToken,
        });

        if (result?.success && result?.user) {
          const memberData = {
            ...result.user,
            last_login: result.last_login,
          };

          localStorage.setItem("member", JSON.stringify(memberData));
          window.dispatchEvent(new Event("authChanged"));
          navigate("/membershipdashboard");
        } else {
          setMessage(result?.message || "Microsoft signup failed.");
        }
      } catch (error) {
        console.error("Microsoft redirect error:", error);
        setMessage(error?.message || "Microsoft signup failed.");
      } finally {
        setLoading(false);
      }
    };

    finishMicrosoftSignup();
  }, [instance, navigate]);

  const goNext = (user) => {
    localStorage.setItem("member", JSON.stringify(user));
    window.dispatchEvent(new Event("authChanged"));
    navigate("/membershipdashboard");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setLoading(true);
        setMessage("");

        const result = await api.post("googleLogin.php", {
          access_token: tokenResponse.access_token,
        });

        if (result?.success && result?.user) {
          const memberData = {
            ...result.user,
            last_login: result.last_login,
          };

          setMessage("Signup successful! Redirecting to dashboard...");

          setTimeout(() => {
            goNext(memberData);
          }, 1000);
        } else {
          setMessage(result?.message || "Google signup failed");
        }
      } catch (err) {
        console.error("Google signup error:", err);
        setMessage("Google signup error");
      } finally {
        setLoading(false);
      }
    },
    onError: () => {
      setMessage("Google sign-up failed.");
    },
  });

  const handleMicrosoftLogin = async () => {
    try {
      setLoading(true);
      setMessage("");
      await instance.loginRedirect({
        ...loginRequest,
        prompt: "select_account",
      });
    } catch (error) {
      console.error("Microsoft login error:", error);
      setMessage(error?.message || "Microsoft sign-in failed.");
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirm_password) {
      setMessage("Passwords do not match");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const signupResult = await api.post("registerMember.php", {
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        auth_provider: "manual",
      });

      if (signupResult?.success) {
        setMessage("Signup successful. Please check your email.");

        setTimeout(() => {
          navigate("/login", {
            state: { membershipType },
          });
        }, 2000);
        return;
      }

      setMessage(signupResult?.message || "Signup failed");
    } catch (error) {
      console.error("Signup error:", error);
      setMessage(error.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const controlClass =
    "w-full h-[48px] px-4 rounded-lg border border-gray-300 text-sm flex items-center justify-center gap-3 hover:bg-gray-50 transition disabled:opacity-60";

  const inputClass =
    "w-full h-[48px] px-4 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-yellow-200";

  return (
    <section className="min-h-screen bg-brand-cream flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-brand">Create Account</h1>
          <p className="text-gray-600 mt-2">
            Join as a member to continue.
          </p>
        </div>

        {membershipType && (
          <p className="text-center text-sm text-gray-600 mb-4">
            Selected membership: <strong>{membershipType}</strong>
          </p>
        )}

        <div className="space-y-3 mb-6">
          <button
            type="button"
            onClick={() => googleLogin()}
            className={controlClass}
            disabled={loading}
          >
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google"
              className="w-5 h-5"
            />
            <span className="text-gray-700">Continue with Google</span>
          </button>

          <button
            type="button"
            onClick={handleMicrosoftLogin}
            className={controlClass}
            disabled={loading}
          >
            <div className="grid grid-cols-2 gap-[2px] w-5 h-5 overflow-hidden rounded-sm">
              <div className="bg-[#F25022]" />
              <div className="bg-[#7FBA00]" />
              <div className="bg-[#00A4EF]" />
              <div className="bg-[#FFB900]" />
            </div>
            <span className="text-gray-700">Continue with Microsoft</span>
          </button>
        </div>

        <div className="relative my-6">
          <div className="border-t" />
          <span className="absolute left-1/2 -translate-x-1/2 -top-3 bg-white px-3 text-sm text-gray-500">
            Or sign up with email
          </span>
        </div>

        <form onSubmit={handleSignup} className="space-y-4">
          <input
            type="text"
            name="full_name"
            placeholder="Full Name"
            value={formData.full_name}
            onChange={handleChange}
            className={inputClass}
            required
          />

          <input
            type="email"
            name="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={handleChange}
            className={inputClass}
            required
          />

          <input
            type="password"
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            className={inputClass}
            required
          />

          <input
            type="password"
            name="confirm_password"
            placeholder="Confirm Password"
            value={formData.confirm_password}
            onChange={handleChange}
            className={inputClass}
            required
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full h-[48px] bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition disabled:opacity-60"
          >
            {loading ? "Creating..." : "Create Account"}
          </button>
        </form>

        {message && (
          <p
            className={`text-sm text-center mt-4 ${
              message.toLowerCase().includes("successful") ||
              message.toLowerCase().includes("redirecting")
                ? "text-green-600"
                : "text-red-500"
            }`}
          >
            {message}
          </p>
        )}

        <p className="text-center text-sm text-gray-600 mt-6">
          Already have an account?{" "}
          <Link
            to="/login"
            state={{ membershipType }}
            className="text-brand font-semibold hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
    </section>
  );
}